import type { FormEvent } from "react";
import { Alerta, Button, MontoField, Row, Select, Stack, TextField, type OpcionSelect } from "@/components";
import type { MedioPago, Moneda } from "@/lib/api";
import { formatMoneda } from "@/lib/format";
import { necesitaTipoCambio, OPCIONES_MEDIO, type ErroresCobro, type FormCobro } from "@/lib/ventas";

export interface FormularioCobroProps {
  /** "Registrar cobro", "Editar cobro", "Registrar devolución" o "Editar devolución" (lo muestra el `Dialog` que lo envuelve). */
  titulo: string;
  form: FormCobro;
  cambiar: <K extends keyof FormCobro>(campo: K, valor: FormCobro[K]) => void;
  errores: ErroresCobro;
  monedaCuenta: Moneda;
  /** Saldo de la cuota en moneda de cuenta; null para devoluciones (y al editar). */
  saldo: string | null;
  /** Lo que representa el monto en la moneda de cuenta (`equivalenteEnCuenta`); null si todavía no se puede calcular. */
  equivalente: string | null;
  onSubmit: (e: FormEvent) => void;
  onCancelar: () => void;
  guardando: boolean;
  error: string | null;
  /** Cajas para elegir; null = una sola caja activa (no se pregunta: va a la principal). */
  opcionesCaja: OpcionSelect[] | null;
}

/** Cobro de una cuota o devolución de una venta desistida: mismos campos. Sin `<Dialog>`: lo envuelve la ficha. */
export function FormularioCobro({ titulo, form, cambiar, errores, monedaCuenta, saldo, equivalente, onSubmit, onCancelar, guardando, error, opcionesCaja }: FormularioCobroProps) {
  return (
    <form onSubmit={onSubmit} noValidate aria-label={titulo}>
      <Stack espacio={4}>
        {saldo !== null && <p className="text-chico text-texto-suave">{`Saldo de la cuota: ${formatMoneda(saldo, monedaCuenta)}`}</p>}
        <TextField etiqueta="Fecha" type="date" value={form.fecha} onChange={(e) => cambiar("fecha", e.target.value)} error={errores.fecha} />
        <MontoField etiqueta="Monto" valor={form.monto} onCambio={(v) => cambiar("monto", v)} moneda={form.moneda} onCambioMoneda={(m) => cambiar("moneda", m)} error={errores.monto} />
        {necesitaTipoCambio(form.moneda, monedaCuenta) && (
          <TextField etiqueta="Tipo de cambio" inputMode="decimal" ayuda="ARS por 1 USD" value={form.tipo_cambio} onChange={(e) => cambiar("tipo_cambio", e.target.value)} error={errores.tipo_cambio} />
        )}
        {equivalente !== null && <p className="text-chico text-texto-suave">{`Equivale a ${formatMoneda(equivalente, monedaCuenta)}`}</p>}
        <Select etiqueta="Medio" opciones={OPCIONES_MEDIO} valor={form.medio} onCambio={(v) => cambiar("medio", v as MedioPago)} />
        {opcionesCaja && <Select etiqueta="Caja" opciones={opcionesCaja} valor={form.caja_id} onCambio={(v) => cambiar("caja_id", v)} placeholder="Elegí la caja" />}
        <TextField etiqueta="Nota" ayuda="Opcional" value={form.nota} onChange={(e) => cambiar("nota", e.target.value)} error={errores.nota} />
        <Alerta texto={error} />
        <Row justificar="fin" espacio={2}>
          <Button variante="secundario" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" cargando={guardando}>
            Guardar
          </Button>
        </Row>
      </Stack>
    </form>
  );
}
